import { makeAutoObservable, runInAction } from 'mobx'
import Settings from '../components/settings'
import * as _ from 'lodash/fp'
import { ValidateType } from './validateType'

/**
 * @typedef {{
 *   valid: boolean,
 *   data: Data,
 *   participants: {[id:string]:ParticipantValidationData},
 * }} Root - creates a new type named 'SpecialType'
 */
/**
 * @typedef {{
 *  id: string,
 *   title: string,
 *   subtitle: string,
 *   total_price: number,
 *   discounts: any[],
 * }} Data - creates a new type named 'SpecialType'
 */
/**
 * @typedef {{
 *  valid: boolean,
 *   data: Data2,
 *   errors: Errors,
 * }} ParticipantValidationData - creates a new type named 'SpecialType'
 */
/**
 * @typedef {{
 *  price: number,
 *   discounts: any[],
 * }} Data2 - creates a new type named 'SpecialType'
 */
/**
 * @typedef {{
 *  duplicated_participant: string
 * }} Errors - creates a new type named 'SpecialType'
 */
export class ValidateAddParticipantController {
  /**
   * @type {ValidateAddParticipantController}
   */
  static instance = null
  get isAllValid() {
    if(!this.validation_data) {
      return true;
    }

    let isAllValid = _.pipe(
      () => this.validation_data,
      Object.values,
      _.all(eg => {
        let pp = _.pipe(
          () => eg,
          eg => eg.participants,
          Object.values,
          _.all(p => p.valid)
        )
        return eg.valid && pp()
      })
    )
    return isAllValid()
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new ValidateAddParticipantController()
    }
    return this.instance
  }

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * @type {{[id:string]:{valid:boolean, errors?:{[id:string]:string},participants:*}}}
   */
  validation_data = {}
  loading = false
  /**
   * @returns {{ groupErrors: {}, participantErrors: {} }}
   */
  get errors() {
    let es = { groupErrors: {}, participantErrors: {} }
    Object.entries(this.validation_data ?? {}).forEach(([k, v]) => {
      if (v.valid === false) {
        es.groupErrors[k] = v.errors
      }
      Object.entries(v.participants ?? {}).forEach(([id, p]) => {
        if (p.valid === false) {
          es.participantErrors[id] = p.errors
        }
      })
    })
    return es
  }
  /**
   * @param {import('./eventGroup').EventGroup[]} eventGroups
   * @param {import('./participant').Participant[]} ps
   * @param {import('./customer').Customer} customer
   * @param coupon
   * @param {boolean} isAddAll
   * @param otherData
   */
  async validate(eventGroups, ps, customer, coupon, isAddAll, otherData = {}) {
    this.validateParticipantFields(eventGroups,ps)

    if (!this.isAllValid) {
      return false
    }

    this.loading = true
    this.validation_data = {}
    const validation = await this.validateCart(eventGroups, ps, customer, coupon, isAddAll, otherData)

    runInAction(() => {
      this.validation_data = validation
      this.loading = false
    })

    return validation
  }

  validateParticipantFields(eventGroups, ps) {
    const fields = {
      name: {
        required: true
      },
      position: {
        required: true,
        callback: data => {
          return ValidateType.validateLength(data, 30)
        }
      },
      email: {
        required: true,
        callback: data => {
          return ValidateType.validateEmail(data)
        }
      }
    }

    eventGroups.forEach(event_group => {
      this.validation_data[event_group.id] = { valid: true, participants: {} }
      this.validation_data[event_group.id].valid = true
    })

    for (const p of ps) {
      let validation = {
        valid: true,
        fields: {}
      }

      Object.entries(fields).forEach(([key, field]) => {
        if (field.required) {
          let required_validation = ValidateType.validateRequired(p[key])
          if (required_validation !== true) {
            validation.valid = false
            validation.fields[key] = {
              valid: false,
              error: required_validation
            }
            return
          }
        }
        if (typeof field.callback === 'function') {
          let callback_validation = field.callback(p[key])
          if (callback_validation !== true) {
            validation.valid = false
            validation.fields[key] = {
              valid: false,
              error: callback_validation
            }
            return
          }
        }

        validation.fields[key] = {
          valid: true
        }
      })

      eventGroups.forEach(event_group => {
        if (!validation.valid) {
          this.validation_data[event_group.id].valid = false
        }
        this.validation_data[event_group.id].participants[p.id] = validation
      })
    }
  }

  getErrorMessageForField(fieldname, participantId) {
    return _.pipe(
      v => v.validation_data ?? {},
      Object.values,
      _.map(
        _.pipe(
          _.prop('participants'),
          _.prop(participantId),
          _.prop('fields'),
          _.prop(fieldname),
          f => (f?.valid ? null : f?.error)
        )
      ),
      _.compact,
      _.head
    )(this)
  }
  /**
   * @param {import('./eventGroup').EventGroup[]} eventGroups
   * @param {import('./participant').Participant[]} ps
   * @param {import('./customer').Customer} customer
   * @param coupon
   * @param {boolean} isAddAll
   * @param otherData
   * @returns {Promise<{[id:string]:Root}>}
   */
  async validateCart(eventGroups, ps, customer, coupon, isAddAll, otherData = {}) {
    return await fetch(
      Settings.getSetting('/cart/validation/add-participants', 'cart'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cart_items: Object.fromEntries(eventGroups.map(eg => [eg.id, eg])),
          participants: Object.fromEntries(ps.map(p => [p.id, p])),
          customer,
          isAddAll,
          coupon,
          ...otherData
        })
      }
    ).then(response => response.json())
  }
}
